import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, ArrowRight, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal, MaskedLines, MonoLabel } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDRESS_LINES = ["260 Peachtree Street", "Suite 2200", "Atlanta, GA 30303"];
const MAPS_QUERY = "260 Peachtree Street, Suite 2200, Atlanta, GA 30303";

const inputCls =
  "w-full h-12 bg-white border border-blue-900/15 px-4 text-sm font-medium text-navy placeholder:text-navy/35 focus:outline-none focus:ring-2 focus:ring-royal-bright focus:border-royal-bright transition-[box-shadow,border-color] duration-150";

const channels = [
  { icon: Phone, label: "Call 24/7", value: PHONE_DISPLAY, sub: "Humans answer, day or night", href: PHONE_TEL, testid: "contact-phone-card" },
  { icon: Mail, label: "Email dispatch", value: EMAIL, sub: "Replies within the hour", href: `mailto:${EMAIL}`, testid: "contact-email-card" },
  { icon: Clock, label: "Always open", value: "24 / 7 / 365", sub: "Nights, weekends, holidays", href: null, testid: "contact-hours-card" },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/messages`, form);
      toast.success("Message sent", { description: "A live human will get back to you shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message", { description: `Please call ${PHONE_DISPLAY} instead.` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section data-testid="contact-hero" className="bg-navy-deep noise-dark pt-40 pb-20">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 relative z-10">
          <MonoLabel light className="mb-6">Contact Way2Go</MonoLabel>
          <MaskedLines
            lines={["TALK TO", "A HUMAN."]}
            className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-[12vw] sm:text-[8vw] lg:text-[6.5rem]"
          />
          <p className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed font-medium">
            No chatbots, no ticket queues, no "your call is important to us".
            Day or night, a person who can actually dispatch your freight picks
            up.
          </p>
        </div>
      </section>

      <Marquee
        dark
        items={[`call ${PHONE_DISPLAY}`, "humans answer 24/7", "no ai · no call centers", EMAIL]}
      />

      <section data-testid="contact-channels" className="py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-blue-900/10 border border-blue-900/10">
            {channels.map((c, i) => {
              const inner = (
                <>
                  <c.icon size={26} strokeWidth={1.75} className="text-royal group-hover:text-royal-bright transition-colors duration-300" />
                  <p className="mt-5 font-mono text-[11px] tracking-[0.25em] uppercase text-navy/45">{c.label}</p>
                  <p className="mt-2 font-mono text-xl font-bold text-navy break-all">{c.value}</p>
                  <p className="mt-1 text-sm font-medium text-navy/50">{c.sub}</p>
                </>
              );
              const cls = "group block bg-white p-8 hover:bg-blue-50/60 transition-colors duration-300 h-full";
              return (
                <Reveal key={c.label} delay={i * 0.08}>
                  {c.href ? (
                    <a href={c.href} data-testid={c.testid} className={cls}>{inner}</a>
                  ) : (
                    <div data-testid={c.testid} className={cls}>{inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="contact-form-section" className="pb-24 sm:pb-32 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <MonoLabel>Write to us</MonoLabel>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
              Send a message
            </h2>
            <p className="mt-6 text-navy/60 leading-relaxed font-medium max-w-md">
              Questions about coverage, pricing, carrier partnerships or an
              in-flight shipment — drop the details here and we reply fast. For
              anything urgent, calling is always quicker.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                required
                data-testid="contact-name-input"
                placeholder="Your name *"
                value={form.name}
                onChange={set("name")}
                className={inputCls}
              />
              <input
                required
                type="email"
                data-testid="contact-email-input"
                placeholder="Email *"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
              />
              <input
                type="tel"
                data-testid="contact-phone-input"
                placeholder="Phone"
                value={form.phone}
                onChange={set("phone")}
                className={`${inputCls} sm:col-span-2`}
              />
              <textarea
                required
                data-testid="contact-message-input"
                placeholder="How can we help? *"
                value={form.message}
                onChange={set("message")}
                rows={6}
                className={`${inputCls} sm:col-span-2 h-auto py-3 resize-none`}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="sm:col-span-2 mt-2 flex items-center justify-center gap-3 h-14 bg-royal text-white font-display text-lg font-bold tracking-wide shadow-[5px_5px_0px_#040B16] hover:shadow-[2px_2px_0px_#040B16] hover:translate-x-[3px] hover:translate-y-[3px] disabled:opacity-60 disabled:pointer-events-none transition-[box-shadow,transform] duration-150"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : <>SEND MESSAGE <ArrowRight size={18} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-map-section" className="py-24 bg-[#F5F8FD] border-t border-blue-900/10">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-4">
            <MonoLabel>Corporate office</MonoLabel>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
              Find us in<br />Atlanta
            </h2>
            <address
              data-testid="contact-address"
              className="mt-8 not-italic font-mono text-base font-bold text-navy leading-loose"
            >
              {ADDRESS_LINES.map((line) => (
                <span key={line} className="block">{line}</span>
              ))}
            </address>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAPS_QUERY)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions-link"
              className="mt-6 inline-flex items-center gap-2 font-mono text-sm font-bold text-royal hover:text-royal-bright transition-colors duration-200"
            >
              <MapPin size={16} /> GET DIRECTIONS <ArrowUpRight size={14} />
            </a>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-8">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-royal/20 pointer-events-none" aria-hidden="true" />
              <iframe
                title="Way2Go Transport Co. — 260 Peachtree Street, Suite 2200, Atlanta, GA 30303"
                src={`https://www.google.com/maps?q=${encodeURIComponent(MAPS_QUERY)}&output=embed`}
                data-testid="contact-map"
                className="relative w-full h-[380px] sm:h-[460px] border border-blue-900/10 bg-white"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
